import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

export default function RefundCancellationPolicy() {
  useEffect(() => {
    document.title = "Refund & Cancellation Policy | Ezeefit Office Systems";
  }, []);

  useCanonical("https://ezeefitofficesystems.in/refund-cancellation");
  useMetaDescription("Refund and cancellation policy for Ezeefit Office Systems — modular office furniture manufacturer. Order cancellation, refund eligibility, defective product replacement, and dispute resolution for B2B furniture orders.");

  return (
    <div className="pt-20" data-testid="refund-policy-page">
      <Helmet>
        <meta
          name="description"
          content="Refund and cancellation policy for Ezeefit Office Systems — modular office furniture manufacturer. Order cancellation, refund eligibility, defective product replacement, and dispute resolution for B2B furniture orders."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <h1
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-3"
            data-testid="refund-policy-heading"
          >
            Refund & Cancellation Policy
          </h1>
          <p className="text-sm text-[#64748B] mb-8">Last updated: February 2026</p>

          <div className="space-y-8 text-[#334155] leading-relaxed">
            <div>
              <p>
                This Refund & Cancellation Policy ("Policy") governs all
                purchase orders, quotations, work orders and B2B furniture
                supply contracts entered into with{" "}
                <strong>Ezeefit Modular Furniture Private Limited</strong>
                {" "}(operating under the brand "Ezeefit Office Systems").
                By placing an order, signing a proforma invoice, or paying an
                advance against any quotation issued by Ezeefit, the buyer
                ("Client") confirms that they have read, understood and agreed
                to the terms set out below. This Policy is to be read together
                with our{" "}
                <Link to="/terms" className="text-[#F97316] hover:underline">
                  Terms & Conditions
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" className="text-[#F97316] hover:underline">
                  Privacy Policy
                </Link>.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">1. Nature of Our Business (B2B Made-to-Order)</h2>
              <p>
                Ezeefit is a modular office furniture manufacturer that builds
                workstations, chairs, tables, partitions, phone booths, meeting
                pods, filing systems, locker systems and related office furniture
                <strong> primarily on a made-to-order basis</strong> against
                approved purchase orders, signed shop drawings, fabric or finish
                selections and project-specific specifications. Once raw
                materials are procured and production has commenced, the
                manufactured items are unique to the Client's project and
                generally cannot be resold to other buyers. This Policy
                therefore differs from the consumer "easy returns" model
                applicable to ready-stock retail goods.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">2. Order Confirmation & Advance Payment</h2>
              <p>
                Orders are deemed confirmed only on receipt of (i) a signed
                purchase order or signed proforma invoice; (ii) the agreed
                advance payment (typically 50% of order value, unless varied in
                writing); and (iii) sign-off on shop drawings, fabric swatches
                or finish samples (where applicable). The advance payment
                covers procurement of raw materials, design freeze, factory
                slot booking and production planning. Estimated delivery
                timelines start only from the date all three of the above are
                received.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">3. Cancellation by the Client</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Within 48 hours of order confirmation (before production):</strong>{" "}
                  The Client may cancel the order in writing (email to
                  {" "}<a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] hover:underline">eos@ezeefitofficesystems.in</a>).
                  Refund is processed after deducting design / consultation /
                  documentation charges and any third-party expenses already
                  incurred (e.g., bought-out items, hardware, special
                  finishes). The balance is refunded within 15 working days to
                  the original bank account.
                </li>
                <li>
                  <strong>After production has commenced:</strong> Cancellation
                  is generally not permitted for custom-manufactured items. If
                  the Client still elects to cancel, the advance is
                  non-refundable to the extent that materials have been
                  procured, components have been cut, fabricated, or finished,
                  and factory capacity has been blocked. Any unutilised portion
                  (after adjusting for the above costs) is refunded at
                  Ezeefit's discretion.
                </li>
                <li>
                  <strong>Standard / catalogue stock items:</strong> For items
                  supplied from existing stock that have not yet been
                  dispatched, the Client may cancel up to 24 hours before
                  dispatch. A processing fee of up to 5% of order value may
                  apply.
                </li>
                <li>
                  <strong>Change orders (modifications to scope):</strong>{" "}
                  Quantity, finish, layout or specification changes after
                  drawing sign-off are treated as a change order and may
                  attract additional cost and revised timelines. Cancellation
                  of items already affected by a change order is not
                  permitted.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">4. Cancellation by Ezeefit</h2>
              <p>
                Ezeefit reserves the right to cancel or hold an order if (a)
                the agreed advance payment is not received within 7 days of
                proforma invoice; (b) site readiness, drawings or sample
                approvals are pending beyond the agreed timeline; (c) the
                Client breaches material terms of the purchase order; or (d)
                force majeure events affect our ability to manufacture or
                deliver. In case of cancellation by Ezeefit for reasons not
                attributable to the Client, the full advance is refunded to
                the original account within 15 working days.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">5. Damaged, Defective or Short-Supplied Goods</h2>
              <p>
                Ezeefit-manufactured products are quality-checked before
                dispatch. On delivery, the Client (or the Client's authorised
                representative) must inspect packaging and products at the
                point of unloading and record any visible transit damage,
                shortage or wrong-shipment on the delivery challan{" "}
                <strong>at the time of receipt</strong>. Internal/concealed
                defects must be reported within 7 calendar days of installation
                with supporting photographs to{" "}
                <a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] hover:underline">eos@ezeefitofficesystems.in</a>.
              </p>
              <p className="mt-2">
                On verification, Ezeefit will, at its sole discretion, either
                (i) repair the defective component; (ii) replace the defective
                component free of cost; or (iii) issue a credit note adjustable
                against the next invoice. A full monetary refund is generally
                not offered for defects that can be remedied by repair or
                replacement under warranty.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">6. Warranty vs. Refund</h2>
              <p>
                Manufacturing defects within the warranty period are addressed
                under our standard warranty terms (specified on the relevant
                purchase order / invoice). Warranty service is a repair /
                replacement remedy and is separate from a refund. Refunds are
                limited to the specific cases listed in Sections 3, 4 and 5
                above. Wear-and-tear, mishandling, unauthorised modification,
                relocation by third parties, exposure to liquids or heat, or
                misuse are not covered.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">7. Refund Mechanism & Timelines</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All approved refunds are processed via NEFT / RTGS / bank transfer to the original remitter's account only. Refunds to alternative accounts are not entertained.</li>
                <li>Standard processing time: <strong>15 working days</strong> from the date the refund is approved in writing by Ezeefit's finance team.</li>
                <li>GST and other statutory taxes already deposited with the government will be adjusted as per applicable Indian tax law.</li>
                <li>Bank charges, payment gateway fees, foreign-exchange conversion charges and similar third-party fees are non-refundable.</li>
                <li>For partial refunds, the balance order value remains payable as per the agreed payment schedule.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">8. Non-Refundable Charges</h2>
              <p>
                The following are non-refundable in all cases: (a) design
                consultation, 3D rendering and shop-drawing charges; (b) site
                visit, measurement and project management fees; (c)
                installation labour already deployed; (d) freight, logistics
                and inter-state transport once dispatched; (e) special
                imported or made-to-order finishes, fabrics, glass, hardware
                and electricals procured specifically for the Client's order.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">9. How to Request a Cancellation or Refund</h2>
              <p>
                All cancellation and refund requests must be made <strong>in writing</strong> (email only — verbal requests are not accepted) and must include:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Proforma invoice / purchase order number</li>
                <li>Date of order and date of advance payment</li>
                <li>Specific items being cancelled / returned</li>
                <li>Reason for cancellation or refund</li>
                <li>Photographs (if reporting damage or defect)</li>
                <li>Bank details of the original remitter</li>
              </ul>
              <p className="mt-3">
                Send the request to{" "}
                <a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] hover:underline font-semibold">
                  eos@ezeefitofficesystems.in
                </a>{" "}
                with the subject line "Refund Request — [PO Number]". Our
                finance team will acknowledge within 3 working days and provide
                a final decision within 10 working days.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">10. Dispute Resolution</h2>
              <p>
                In the event of any dispute relating to a cancellation or
                refund, the parties shall first attempt to resolve the matter
                amicably through written communication between authorised
                representatives. If the dispute remains unresolved for 30
                days, it shall be referred to mediation. Any further
                proceedings shall be subject to the exclusive jurisdiction of
                the courts at Mumbai, Maharashtra, India. Indian law shall
                govern the interpretation and enforcement of this Policy.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">11. Amendments to this Policy</h2>
              <p>
                Ezeefit may update this Policy from time to time to reflect
                operational, legal or regulatory changes. The version
                applicable to a given order is the one published on{" "}
                <a href="https://ezeefitofficesystems.in/refund-cancellation" className="text-[#F97316] hover:underline">
                  ezeefitofficesystems.in/refund-cancellation
                </a>{" "}
                on the date the purchase order was confirmed.
              </p>
            </div>

            <div className="border-t border-[#E2E8F0] pt-6">
              <h2 className="text-xl font-bold text-[#0F172A] mb-3">Contact Us</h2>
              <p className="mb-2"><strong>Ezeefit Modular Furniture Private Limited</strong></p>
              <p>6th Floor, A/611-612, Crystal Plaza Premises,</p>
              <p>New Link Road, Opp. Infinity Mall, Andheri West,</p>
              <p>Mumbai 400053, Maharashtra, India</p>
              <p className="mt-2">GSTIN: 27AADCE6117G1ZT</p>
              <p className="mt-2">
                Email:{" "}
                <a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] hover:underline">
                  eos@ezeefitofficesystems.in
                </a>
              </p>
              <p>
                Phone:{" "}
                <a href="tel:+919820596811" className="phone-flash">
                  9820596811
                </a>{" "}
                /{" "}
                <a href="tel:+919820042100" className="text-[#F97316] hover:underline">
                  9820042100
                </a>
              </p>
              <div className="mt-4 flex gap-4 text-sm">
                <Link
                  to="/privacy-policy"
                  className="text-[#F97316] hover:underline"
                  data-testid="refund-policy-privacy-link"
                >
                  View Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-[#F97316] hover:underline"
                  data-testid="refund-policy-terms-link"
                >
                  View Terms & Conditions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
